import { useState } from 'react'
import type { EventType } from '@fellowship/shared'
import { EVENT_TYPES } from '../ui/eventTypes'

const KEYS = Object.keys(EVENT_TYPES) as EventType[]

interface Props {
  value: EventType
  onChange: (type: EventType) => void
}

export function EventTypePicker({ value, onChange }: Props) {
  const [pressed, setPressed] = useState<EventType | null>(null)

  return (
    <div>
      <div className="flex items-baseline justify-between mb-5">
        <span className="text-[11.5px] font-bold uppercase tracking-[0.06em] text-muted">Event type</span>
        <span className="text-[12.5px] font-medium text-muted">Tap to select</span>
      </div>
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
        role="radiogroup"
        aria-label="Event type"
      >
        {KEYS.map((key) => {
          const t = EVENT_TYPES[key]
          const selected = value === key
          const isPressed = pressed === key
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={selected}
              onMouseDown={() => setPressed(key)}
              onMouseUp={() => setPressed(null)}
              onMouseLeave={() => setPressed(null)}
              onClick={() => onChange(key)}
              className="flex flex-col items-center gap-[11px] py-5 px-2 pb-4 rounded-2xl border cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2"
              style={{
                background: selected ? t.color : 'var(--surface-2)',
                borderColor: selected ? t.color : 'transparent',
                boxShadow: selected ? '0 1px 2px rgba(31,26,46,.04), 0 12px 28px rgba(31,26,46,.06)' : 'none',
                transform: isPressed ? 'scale(0.97)' : 'scale(1)',
                transition: 'background .15s, transform .12s, box-shadow .15s',
                outline: undefined,
              }}
            >
              <div
                className="w-[46px] h-[46px] rounded-full inline-flex items-center justify-center"
                style={{
                  background: selected ? 'rgba(255,255,255,.22)' : t.tint,
                  color: selected ? '#ffffff' : t.color,
                  transition: 'background .15s, color .15s',
                }}
              >
                <t.icon size={23} strokeWidth={1.9} />
              </div>
              <span
                className="text-[13.5px] font-semibold text-center leading-[1.2]"
                style={{ color: selected ? '#ffffff' : 'var(--ink)' }}
              >
                {t.label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
